import React from 'react';
import { ArrowRight } from 'lucide-react';

const intelligenceCards = [
  {
    number: '01',
    title: 'Equity Intelligence™',
    description: 'Deep company intelligence, financial analysis, valuation and investment context.',
    href: 'https://www.thebtree.com/equity-intelligence',
  },
  {
    number: '02',
    title: 'Sector Intelligence™',
    description: 'Sector structure, economics, profit pools, disruption, risks and future direction.',
    href: 'https://www.thebtree.com/sector-intelligence',
  },
  {
    number: '03',
    title: 'Legal Intelligence™',
    description: 'Structured intelligence on law, judgments, regulation and practical implications.',
    href: 'https://www.thebtree.com/legal-intelligence',
  },
  {
    number: '04',
    title: 'Professional Intelligence™',
    description: 'Finance, accounting, governance, risk and AI intelligence for modern professionals.',
    href: 'https://www.thebtree.com/professional-intelligence',
  },
  {
    number: '05',
    title: 'Civil Services Intelligence™',
    description: 'Governance, economy, policy, international affairs and administrative thinking.',
    href: 'https://www.thebtree.com/civil-services-intelligence',
  },
];

const exploreCards = [
  {
    tag: 'Equity & Sector',
    title: 'Research Intelligence',
    description: 'Explore company and sector reports built around economics, financial performance, valuation, risk and forward intelligence.',
    href: 'https://www.thebtree.com/equity-intelligence',
  },
  {
    tag: 'Legal Intelligence',
    title: 'Law, Regulation & Judgments',
    description: 'Navigate legal developments through structured research, practical frameworks and decision-relevant interpretation.',
    href: 'https://www.thebtree.com/legal-intelligence',
  },
  {
    tag: 'Professional Intelligence',
    title: 'Learn. Apply. Decide. Lead.',
    description: 'Institutional-quality intelligence across finance, valuation, governance, risk, tax, corporate law and AI.',
    href: 'https://www.thebtree.com/professional-intelligence',
  },
  {
    tag: 'Civil Services',
    title: 'Understand India. Think Administratively.',
    description: 'Governance, economy, policy, international relations and administrative intelligence built for deeper understanding.',
    href: 'https://www.thebtree.com/civil-services-intelligence',
  },
  {
    tag: 'Sector Intelligence',
    title: 'Understand the Industry Before the Company.',
    description: 'See sector structure, economics, capital flows, disruption, competitive forces, risks and long-term direction.',
    href: 'https://www.thebtree.com/sector-intelligence',
  },
  {
    tag: 'Membership',
    title: 'Unlock the Complete Intelligence.',
    description: 'Move from open intelligence to deeper reports, analysis and the complete BTree library through membership.',
    href: 'https://www.thebtree.com/subscription',
  },
];

const plans = [
  {
    title: 'Free',
    tag: 'OPEN ACCESS',
    description: 'For readers discovering BTree and exploring selected intelligence.',
    items: ['Selected intelligence and open content', 'Explore BTree\'s five intelligence systems', 'Preview the BTree research approach'],
    href: 'https://www.thebtree.com/',
    featured: false,
  },
  {
    title: 'Basic',
    tag: 'MEMBERSHIP',
    description: 'For regular readers who want broader access across BTree intelligence.',
    items: ['Expanded intelligence access', 'Deeper report content', 'Access across eligible BTree libraries'],
    href: 'https://www.thebtree.com/subscription',
    featured: false,
  },
  {
    title: 'Premium',
    tag: 'FULL ACCESS',
    description: 'For readers who want the deepest level of BTree intelligence and complete eligible report access.',
    items: ['Complete eligible intelligence reports', 'Deep analysis and forward intelligence', 'Broadest access across the BTree platform'],
    href: 'https://www.thebtree.com/subscription',
    featured: true,
  },
];

const SectionHeader = ({ kicker, title, subtitle }) => (
  <div className="flex justify-between items-end gap-6 mb-12">
    <div>
      <div className="text-[12px] font-extrabold tracking-[0.17em] uppercase text-[#C8A84B]">{kicker}</div>
      <h2 className="mt-2 font-[Georgia,serif] text-[42px] leading-[1.15] text-[#0b1830] font-medium">{title}</h2>
    </div>
    <p className="max-w-[500px] text-[#536177] leading-[1.65] hidden md:block">{subtitle}</p>
  </div>
);

const FeaturedReportsPremium = () => {
  return (
    <>
      <section id="intelligence" className="py-20 md:py-24 px-6 md:px-10 lg:px-12 bg-white">
        <div className="max-w-[1180px] mx-auto">
          <SectionHeader
            kicker="Explore BTree"
            title="Five Intelligence Systems"
            subtitle="Choose the intelligence domain that matches the decision, profession or subject you want to understand."
          />

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
            {intelligenceCards.map((card) => (
              <a
                key={card.number}
                href={card.href}
                target="_blank"
                rel="noopener noreferrer"
                className="group border border-[#e1e7ef] rounded-[10px] p-5 min-h-[245px] transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_14px_35px_rgba(10,29,53,0.08)] hover:border-[#d8c08a] bg-[#fffdf9]"
              >
                <div className="text-[12px] font-bold text-[#8b97a8]">{card.number}</div>
                <h3 className="font-[Georgia,serif] text-[22px] leading-[1.18] text-[#0b1830] mt-8 mb-3">{card.title}</h3>
                <p className="text-[14px] leading-[1.6] text-[#536177]">{card.description}</p>
                <span className="mt-6 block font-extrabold text-[#0b1830]">Explore →</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24 px-6 md:px-10 lg:px-12 bg-[#f7f9fc]">
        <div className="max-w-[1180px] mx-auto">
          <SectionHeader
            kicker="Intelligence Library"
            title="Start Exploring"
            subtitle="Enter each BTree intelligence library to discover the latest and featured research available on the platform."
          />

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {exploreCards.map((card) => (
              <a
                key={card.title}
                href={card.href}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex h-full flex-col rounded-[10px] border border-[#e1e7ef] bg-[#fffdf9] p-6 transition-colors hover:border-[#d8c08a]"
              >
                <div className="text-[11px] font-extrabold tracking-[0.12em] uppercase text-[#C8A84B]">{card.tag}</div>
                <h3 className="mt-4 font-[Georgia,serif] text-[24px] leading-[1.25] text-[#0b1830]">{card.title}</h3>
                <p className="mt-3 text-[14px] leading-[1.65] text-[#536177] flex-1">{card.description}</p>
                <span className="mt-5 font-bold text-[#0b1830]">Open →</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24 px-6 md:px-10 lg:px-12 bg-white">
        <div className="max-w-[1180px] mx-auto grid gap-10 md:grid-cols-[0.8fr_1.2fr] items-start">
          <div>
            <div className="text-[12px] font-extrabold tracking-[0.17em] uppercase text-[#C8A84B]">The BTree Standard</div>
            <h2 className="mt-2 font-[Georgia,serif] text-[42px] leading-[1.15] text-[#0b1830]">From information to intelligence.</h2>
            <p className="mt-5 text-[16px] leading-[1.75] text-[#536177]">
              BTree is designed to help readers understand what matters, why it matters and what to watch next.
            </p>
          </div>

          <div className="border-t border-slate-200">
            {[
              ['01', 'Research', 'Relevant information, primary material and credible sources are brought together.'],
              ['02', 'Structure', 'Complex subjects are organised into clear analytical frameworks.'],
              ['03', 'Interpret', 'Facts are connected to economics, risk, policy, professional practice and decision context.'],
              ['04', 'Look Forward', 'Reports identify implications, emerging developments, uncertainties and what deserves attention next.'],
            ].map(([n, heading, text]) => (
              <div key={n} className="grid grid-cols-[55px_1fr] gap-4 border-b border-slate-200 py-5">
                <span className="text-[#8490a0] font-semibold">{n}</span>
                <div>
                  <b className="text-[16px] text-[#0b1830]">{heading}</b>
                  <p className="mt-1 text-[14px] leading-[1.55] text-[#536177]">{text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24 px-6 md:px-10 lg:px-12 bg-[#f5f7fa]">
        <div className="max-w-[1180px] mx-auto">
          <SectionHeader
            kicker="BTree Membership"
            title="Choose how deeply you want to explore."
            subtitle="Start with open intelligence or unlock deeper access through a BTree membership. Current pricing and full entitlements are maintained on the Membership page."
            href="https://www.thebtree.com/subscription"
          />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {plans.map((plan) => (
              <div
                key={plan.title}
                className={`relative flex h-full flex-col rounded-[12px] border bg-white p-6 ${plan.featured ? 'border-[#0b1830] shadow-[0_16px_42px_rgba(10,29,53,0.10)]' : 'border-slate-200'}`}
              >
                {plan.featured && (
                  <span className="absolute right-4 top-4 rounded-full bg-[#0b1830] px-2 py-1 text-[10px] font-extrabold tracking-[0.12em] uppercase text-white">
                    FULL ACCESS
                  </span>
                )}
                <div className="text-[11px] font-extrabold tracking-[0.12em] uppercase text-[#C8A84B]">{plan.tag}</div>
                <h3 className="mt-3 font-[Georgia,serif] text-[28px] text-[#0b1830]">{plan.title}</h3>
                <p className="mt-2 text-[14px] leading-[1.55] text-[#536177] min-h-[48px]">{plan.description}</p>
                <ul className="mt-6 space-y-3">
                  {plan.items.map((item) => (
                    <li key={item} className="flex items-start gap-3 border-t border-slate-200 pt-3 text-[14px] leading-[1.45] text-[#0b1830]">
                      <span className="mt-0.5 text-[#55719f] font-extrabold">✓</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href={plan.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`mt-6 inline-flex items-center justify-center rounded-[7px] border px-4 py-3 text-[14px] font-bold ${plan.featured ? 'bg-[#0b1830] text-white border-[#0b1830]' : 'bg-white text-[#0b1830] border-[#0b1830] hover:bg-[#f3f6fa]'}`}
                >
                  {plan.title === 'Free' ? 'Start Exploring' : `See Current ${plan.title} Plan`}
                </a>
              </div>
            ))}
          </div>

          <p className="mt-6 text-center text-[13px] text-[#536177]">
            For current prices, billing periods, exact access entitlements and applicable terms, see the live Membership page.
          </p>
        </div>
      </section>

      <section className="bg-[#071426] py-20 px-6 md:px-10 lg:px-12 text-white mt-16">
        <div className="max-w-[1180px] mx-auto flex flex-col gap-8 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="text-[12px] font-extrabold tracking-[0.17em] uppercase text-[#a9b9d1]">Go Deeper</div>
            <h2 className="mt-2 max-w-[650px] font-[Georgia,serif] text-[42px] leading-[1.15] text-white">You’ve seen the platform. Now explore the intelligence.</h2>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <a href="https://www.thebtree.com/equity-intelligence" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center rounded-[7px] bg-white px-5 py-3 text-[14px] font-bold text-[#071426]">
              Explore Intelligence
            </a>
            <a href="https://www.thebtree.com/subscription" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center rounded-[7px] border border-white px-5 py-3 text-[14px] font-bold text-white">
              View Membership
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default FeaturedReportsPremium;
